import React from 'react';

const OpenClaw = () => (
  <article className="news-article-body">
    <p className="news-lead">
      In late 2025, Austrian developer Peter Steinberger published an open-source AI agent
      platform called <strong>"Clawd"</strong> — a portmanteau of Claude and claw.
      It went viral almost immediately. Then Anthropic's legal team sent a trademark notice.
      After two rebrands — Moltbot, then OpenClaw — the project reached
      <strong> 180,000+ GitHub stars</strong> by February 2026, becoming one of the
      fastest-growing AI open-source projects in history.
    </p>

    <h2>The Naming Saga: Clawd → Moltbot → OpenClaw</h2>
    <div className="news-timeline">
      <div className="news-timeline-item">
        <span className="news-timeline-date">November 2025</span>
        <div>
          <strong>Clawd</strong> launches on GitHub. The name combines "Claude" (Anthropic's AI)
          with "claw" (as in lobster claw — a playful nod to Claude's name, which itself references
          Claude Shannon). The project provides a self-hosted agent that wraps Claude with
          multi-platform chat integrations.
        </div>
      </div>
      <div className="news-timeline-item">
        <span className="news-timeline-date">Late December 2025</span>
        <div>
          Anthropic's legal team contacts Steinberger citing trademark concerns over
          "Clawd" being too similar to "Claude." Project rebrands to
          <strong>Moltbot</strong> — referencing a lobster's moult (when it sheds its shell
          to grow), and "bot." Stars continue accumulating despite the rename.
        </div>
      </div>
      <div className="news-timeline-item">
        <span className="news-timeline-date">January 29, 2026</span>
        <div>
          Final rebrand to <strong>OpenClaw</strong> after trademark research cleared the name.
          The project has now accumulated over <strong>180,000 GitHub stars</strong> and
          20,000+ forks — making it one of the top-starred AI repositories ever.
        </div>
      </div>
      <div className="news-timeline-item">
        <span className="news-timeline-date">February 2026</span>
        <div>
          Creator Peter Steinberger announces he is <strong>joining OpenAI</strong> — while
          clarifying OpenClaw will continue as an independent open-source project under
          foundation governance.
        </div>
      </div>
    </div>

    <h2>What OpenClaw Actually Does</h2>
    <p>
      OpenClaw is a <strong>self-hosted AI agent platform</strong> that bridges frontier
      language models with the chat tools people already use every day. Its core value
      proposition: run a personal AI agent that operates continuously in the background,
      accessible via your existing communication apps.
    </p>
    <p>Supported integrations:</p>
    <ul>
      <li><strong>Chat platforms</strong>: WhatsApp, Telegram, Discord, Slack, Microsoft Teams</li>
      <li><strong>AI providers</strong>: Claude (Anthropic), GPT (OpenAI), Gemini (Google), Ollama (local models)</li>
      <li><strong>Agent capabilities</strong>: Web search, code execution, file management, scheduling</li>
    </ul>

    <h2>Why It Went Viral</h2>
    <p>
      OpenClaw solved a real pain point: frontier AI models are powerful but require
      switching to a dedicated app. OpenClaw brought Claude into WhatsApp — where hundreds
      of millions of people are already active. The combination of:
    </p>
    <ul>
      <li>Self-hosted (privacy-preserving, no third-party data sharing)</li>
      <li>Multi-LLM (not locked to any single provider)</li>
      <li>Familiar chat UX (no new app to install or learn)</li>
      <li>Open-source MIT license (fully auditable, forkable)</li>
    </ul>
    <p>
      ...made it immediately practical for developers and power users who wanted AI
      without trading off privacy or vendor lock-in.
    </p>

    <h2>Anthropic's Parallel Move: Claude Code Computer Use</h2>
    <p>
      Ironically, while OpenClaw was growing on the back of Claude's API, Anthropic was
      building its own agentic vision. In March 2026, Anthropic officially added
      <strong>"Computer Use"</strong> to Claude Code — allowing Claude to directly control
      computer interfaces (viewing screens, clicking buttons, filling forms) without
      pre-built APIs. A new <strong>"Channels" function</strong> also enabled external
      webhooks and alerts to be injected into running Claude Code sessions, enabling
      long-running background agents. OpenClaw and Claude Code now exist as complementary
      tools: OpenClaw for chat-first personal agents, Claude Code for developer and
      engineering workflows.
    </p>

    <div className="news-insight-box">
      <h3>Key Takeaway</h3>
      <p>
        OpenClaw's journey from "Clawd" to 180,000 GitHub stars illustrates two important
        dynamics: (1) developers will rapidly build and distribute what frontier AI labs
        haven't productised yet, and (2) trademark disputes in AI are becoming increasingly
        common as names, personas, and brand associations become commercially valuable.
        The platform's success — driven entirely by community adoption — is a template
        for open-source AI tooling in the agent era.
      </p>
    </div>
  </article>
);

export default OpenClaw;
